<template>
  <div>
    <h2 class="text-left alert" :class="[removed ? 'alert-danger' : 'alert-success']">
      Mastitis/Milk Quality
      <a class="btn btn-sm" @click="$emit('onRemoveSection', stepName)">
        <span class="glyphicon" :class="[removed ? 'glyphicon-plus' : 'glyphicon-remove']"></span>
      </a>
    </h2>
    <template v-if="!removed">
      <report-comment field_name="heading_mastitis_milk_quality" :model="model.COMMENTS" :heading="true">
      </report-comment>

      <subsection title="Dry Cow Sales" :step-name="stepName" subsection="dry_cow_used">
        <p class="text-left">Product quantities purchased by season.</p>
        <report-comment field_name="heading_dry_cow_used" :model="model.COMMENTS" :heading="true"> </report-comment>
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Animal Name</th>
              <th>Product</th>
              <th>{{ seasonStartYear - 2 }}-{{ (seasonStartYear - 1).toString().substr(2, 2) }}</th>
              <th>{{ seasonStartYear - 1 }}-{{ seasonStartYear.toString().substr(2, 2) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(sale, index) in model.CHARTS.dc_get_drycow_sales" :key="'dgds_' + index">
              <td class="text-left">{{ sale['Animal name'] }}</td>
              <td class="text-left">{{ sale['Product'] }}</td>
              <td class="text-left">{{ sale['qty_ls'] }}</td>
              <td class="text-left">{{ sale['qty_ts'] }}</td>
            </tr>
          </tbody>
        </table>
      </subsection>

      <subsection title="Actual Dry Cow Strategy" :step-name="stepName" subsection="actual_dry_cow_strategy">
        <div v-for="farm in model.FARMS" :key="'actual_dry_cow_strategy_' + farm.id">
          <h4 class="text-left">{{ farm.farm_label }}</h4>
          <report-comment
            :field_name="`farm_${farm.id}-heading_actual_dry_cow_strategy`"
            :model="model.COMMENTS"
            :heading="true"
          >
          </report-comment>
        </div>
      </subsection>

      <subsection title="Seasonal Mastitis" :step-name="stepName" subsection="mastitis_milk_quality">
        <report-comment field_name="heading_chart_mastitis_milk_quality" :model="model.COMMENTS" :heading="true">
        </report-comment>
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Farm</th>
              <th>Season</th>
              <th>Cows Treated</th>
              <th>Percent Cows Wintered</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(smmq, index) in model.CHARTS.seasonal_mastitis" :key="'seasonal_m_' + index">
              <td class="text-left">{{ smmq[0] }}</td>
              <td class="text-left">{{ smmq[1] }}</td>
              <td class="text-left">{{ smmq[2] }}</td>
              <td class="text-left">{{ smmq[3] }}</td>
            </tr>
          </tbody>
        </table>
      </subsection>

      <subsection title="Mastitis Treatments" :step-name="stepName" subsection="mastitis_treatments">
        <report-comment
          field_name="heading_mastitis_treatments"
          :model="model.COMMENTS"
          :heading="true"
        ></report-comment>

        <div v-for="farm in model.FARMS" :key="'mastitis_t_' + farm.id">
          <h4>{{ farm.farm_label }}</h4>
          <p>Estimated number of cows treated per month based on purchases.</p>
          <div class="step-section">
            <bar
              :chart-data="treatmentsChartData(farm.id)"
              :options="treatmentsChartOptions"
              :ref="'farm_' + farm.id + '-mastitis_treatments_chart'"
            >
            </bar>

            <table class="table table-bordered">
              <thead>
                <tr>
                  <th></th>
                  <th
                    v-for="(date, index) in chartMastitisTreatmentsDates(farm.id)"
                    :key="'mastitis_t_headers_' + index"
                  >
                    {{ date }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(product, productIndex) in chartMastitisTreatmentsProducts(farm.id)"
                  :key="farm.id + '_mt_data' + productIndex"
                >
                  <td>{{ product }}</td>
                  <td
                    v-for="(date, dateIndex) in chartMastitisTreatmentsDates(farm.id)"
                    :key="farm.id + '_mt_dates_' + dateIndex"
                  >
                    {{ getProductValueForDate(farm.id, product, date) || '' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </subsection>

      <subsection
        :title="farm.farm_label"
        :step-name="stepName"
        :subsection="`farm_${farm.id}-mmq`"
        v-for="farm in model.FARMS"
        :key="'farm_fields_' + farm.id"
      >
        <dl class="dl-horizontal">
          <dt>Avg. BMSCC Last Season</dt>
          <dd>{{ model[`farm_${farm.id}-mmq_bmscc`] || '-' }}</dd>
          <report-comment :field_name="`farm_${farm.id}-mmq_bmscc`" :model="model.COMMENTS"></report-comment>
        </dl>
      </subsection>
    </template>
  </div>
</template>

<script>
import mixin from './mixin'
import Bar from '@/components/charts/Bar'

export default {
  name: 'MastitisMilkQuality',
  mixins: [mixin],
  props: ['model'],
  components: {
    Bar,
  },
  methods: {
    treatmentsChartData: function (farmId) {
      var self = this

      var dates = this.chartMastitisTreatmentsDates(farmId)
      var products = this.chartMastitisTreatmentsProducts(farmId)

      var datasets = []

      products.forEach(function (productName) {
        var product = {
          label: productName,
          backgroundColor: '#' + ((Math.random() * 0xffffff) << 0).toString(16),
          data: [],
        }
        dates.forEach(function (dateName) {
          product['data'].push(self.getProductValueForDate(farmId, productName, dateName))
        })

        datasets.push(product)
      })

      return {
        labels: dates,
        datasets: datasets,
      }
    },
    chartMastitisTreatmentsDates: function (farmId) {
      return Array.from(
        new Set(
          (this.model.CHARTS.mastitis_treatments[farmId] || []).map((x) => x['Date'])
        )
      ).reverse()
    },
    chartMastitisTreatmentsProducts: function (farmId) {
      return Array.from(
        new Set(
          (this.model.CHARTS.mastitis_treatments[farmId] || []).filter((x) => x['Product']).map((x) => x['Product'])
        )
      )
    },
    getProductValueForDate: function (farmId, product, dateName) {
      var value = this.model.CHARTS.mastitis_treatments[farmId].filter(
        (x) => x['Date'] == dateName && x['Product'] == product
      )
      if (value.length > 0) {
        return value[0]['Cows Treated']
      }
      return 0
    },
  },
  computed: {
    treatmentsChartOptions: function () {
      return {
        responsive: true,
        scales: {
          xAxes: [
            {
              stacked: true,
              categoryPercentage: 0.5,
              barPercentage: 1,
            },
          ],
          yAxes: [
            {
              stacked: true,
            },
          ],
        },
      }
    },
  },
}
</script>
